import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../api/axiosInstance';
import base_url from '../../../constants';
import useNotification from '../../common/Notification';
import { WB_SX } from '../../../ui/ux/uiTokens';

const UserManagement = () => {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    // Required fields
    name: '',
    email: '',
    password: '',
    
    // Optional user fields
    user_role: [],
    
    // Optional WhatsApp config fields
    business_id: '',
    waba_id: '',
    app_id: '',
    app_secret_code: '',
    access_token: '',
    phone_number_id: '',
    whatsapp_phone_no: '',
    status: 'INACTIVE',
    subscription_end: '',
    bwc: '',
    dbwc: '',
    adbwc: '',
    automation_enabled: false,
    run_interval_minutes: '',
    next_run_at: '',
  });
  
  const notify = useNotification();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.name || !formData.email || !formData.password) {
      setError('Name, Email, and Password are required fields');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      // Prepare payload - only include fields that have values
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
      };

      // Add optional user fields - join array with comma if multiple roles
      if (formData.user_role && formData.user_role.length > 0) {
        payload.user_role = Array.isArray(formData.user_role) 
          ? formData.user_role.join(',') 
          : formData.user_role;
      }

      // Add optional WhatsApp config fields
      if (formData.business_id) payload.business_id = formData.business_id;
      if (formData.waba_id) payload.waba_id = formData.waba_id;
      if (formData.app_id) payload.app_id = formData.app_id;
      if (formData.app_secret_code) payload.app_secret_code = formData.app_secret_code;
      if (formData.access_token) payload.access_token = formData.access_token;
      if (formData.phone_number_id) payload.phone_number_id = formData.phone_number_id;
      if (formData.whatsapp_phone_no) payload.whatsapp_phone_no = formData.whatsapp_phone_no;
      if (formData.status) payload.status = formData.status;
      if (formData.subscription_end) payload.subscription_end = formData.subscription_end;
      if (formData.bwc) payload.bwc = parseInt(formData.bwc);
      if (formData.dbwc) payload.dbwc = parseInt(formData.dbwc);
      if (formData.adbwc) payload.adbwc = parseInt(formData.adbwc);
      if (formData.automation_enabled !== undefined) payload.automation_enabled = formData.automation_enabled;
      if (formData.run_interval_minutes) payload.run_interval_minutes = parseInt(formData.run_interval_minutes);
      if (formData.next_run_at) payload.next_run_at = formData.next_run_at;

      const response = await axiosInstance.post(
        `${base_url}/whatsapp/business/admin/users/create`,
        payload
      );

      if (response.data && response.data.success) {
        notify('User and WhatsApp Business configuration created successfully', 'success');
        handleClose();
      } else {
        throw new Error('Failed to create user');
      }
    } catch (err) {
      let errorMessage = 'Failed to create user';
      
      if (err.response?.data) {
        if (err.response.data.errors) {
          // Handle validation errors
          const errors = err.response.data.errors;
          errorMessage = Object.values(errors).flat().join(', ');
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      }
      
      setError(errorMessage);
      notify(errorMessage, 'error');
      console.error('Error creating user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      user_role: [],
      business_id: '',
      waba_id: '',
      app_id: '',
      app_secret_code: '',
      access_token: '',
      phone_number_id: '',
      whatsapp_phone_no: '',
      status: 'INACTIVE',
      subscription_end: '',
      bwc: '',
      dbwc: '',
      adbwc: '',
      automation_enabled: false,
      run_interval_minutes: '',
      next_run_at: '',
    });
    setError('');
    setCreateModalOpen(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create New User
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="textSecondary" paragraph>
          Create new users with WhatsApp Business configuration. All WhatsApp Business fields are optional and can be configured later.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Required fields: Name, Email, Password (minimum 6 characters)
        </Typography>
      </Paper>

      {/* Create User Modal */}
      <Dialog 
        open={createModalOpen} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Create New User with WhatsApp Business Configuration</Typography>
            <Button
              iconButton
              onClick={handleClose}
              sx={{ minWidth: 'auto', p: 1 }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mt: 2, mb: 2, ...WB_SX.sectionTitle }}>
              Required User Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  variant="outlined"
                  helperText="Minimum 6 characters"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="user-role-label">User Role(s)</InputLabel>
                  <Select
                    labelId="user-role-label"
                    id="user-role-select"
                    multiple
                    value={formData.user_role}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        user_role: typeof value === 'string' ? value.split(',') : value,
                      }));
                    }}
                    input={<OutlinedInput label="User Role(s)" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="PERSONAL">PERSONAL</MenuItem>
                    <MenuItem value="WB-USER">WB-USER</MenuItem>
                    <MenuItem value="WB-ADMIN">WB-ADMIN</MenuItem>
                    <MenuItem value="WB-CHAT">WB-CHAT</MenuItem>
                    <MenuItem value="EMAIL">EMAIL</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4, mb: 2, ...WB_SX.sectionTitle }}>
              WhatsApp Business Configuration (Optional)
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Business ID"
                  name="business_id"
                  value={formData.business_id}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WABA ID"
                  name="waba_id"
                  value={formData.waba_id}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="App ID"
                  name="app_id"
                  value={formData.app_id}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="App Secret Code"
                  name="app_secret_code"
                  value={formData.app_secret_code}
                  onChange={handleChange}
                  variant="outlined"
                  type="password"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Access Token"
                  name="access_token"
                  value={formData.access_token}
                  onChange={handleChange}
                  variant="outlined"
                  type="password"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone Number ID"
                  name="phone_number_id"
                  value={formData.phone_number_id}
                  onChange={handleChange}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Phone Number"
                  name="whatsapp_phone_no"
                  value={formData.whatsapp_phone_no}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  variant="outlined"
                >
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Subscription End Date"
                  name="subscription_end"
                  type="datetime-local"
                  value={formData.subscription_end}
                  onChange={handleChange}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Business WhatsApp Credits (BWC)"
                  name="bwc"
                  type="number"
                  value={formData.bwc}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Daily Business WhatsApp Credits (DBWC)"
                  name="dbwc"
                  type="number"
                  value={formData.dbwc}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Additional Daily BWC (ADBWC)"
                  name="adbwc"
                  type="number"
                  value={formData.adbwc}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.automation_enabled}
                      onChange={handleChange}
                      name="automation_enabled"
                    />
                  }
                  label="Automation Enabled"
                />
              </Grid>
              {formData.automation_enabled && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Run Interval (minutes)"
                      name="run_interval_minutes"
                      type="number"
                      value={formData.run_interval_minutes}
                      onChange={handleChange}
                      variant="outlined"
                      inputProps={{ min: 1, max: 1440 }}
                      helperText="Between 1 and 1440 minutes"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Next Run At"
                      name="next_run_at"
                      type="datetime-local"
                      value={formData.next_run_at}
                      onChange={handleChange}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;

